import React, { useEffect, useState, useMemo } from "react";
import { FaPlus } from "react-icons/fa";
import { FiSettings, FiSliders, FiFileText } from "react-icons/fi";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import api from "./../../../api";
import EditorFormalitiesMeta from "components/formalities/admin/modals/EditorFormalitiesMeta.jsx";
export default function AdminPageFormalitiesTemplatesList() {
  const STORAGE_KEY = "admin_templates_open_category";

  const [openCategoryId, setOpenCategoryId] = useState(() => {
    return localStorage.getItem(STORAGE_KEY);
  });
  const navigate = useNavigate();
  // ----------------------------------------------------------------------
  // STATE
  // ----------------------------------------------------------------------
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [metaEditor, setMetaEditor] = useState({
    open: false,
    meta: null,
    mode: "create"
  });
  // ----------------------------------------------------------------------
  // LOAD DATA
  // ----------------------------------------------------------------------
  const load = async () => {
    setLoading(true);
    try {
      const [catRes, tplRes] = await Promise.all([
        api.get("/admin/formalities/categories"),
        api.get("/admin/formalities/templates")
      ]);
      setCategories(catRes.data || []);
      setTemplates(tplRes.data || []);
    } catch (e) {
      console.error("LOAD ERROR", e);
      setError("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  // ----------------------------------------------------------------------
  // META EDITOR
  // ----------------------------------------------------------------------
  const openCreate = () => {
    setMetaEditor({
      open: true,
      mode: "create",
      meta: {
        ftName: "",
        ftCategoryId: "",
        ftStatus: "inactive",
        ftLayout: "slides",
        ftSummary: "",
        ftTotalPriceET: 0,
        ftTotalPriceIT: 0
      }
    });
  };

  const openEditMeta = (tpl) => {
    setMetaEditor({
      open: true,
      mode: "edit",
      meta: { ...tpl }
    });
  };

  const openDuplicate = async (tpl) => {
    try {
      // Hypothèse backend cohérente avec le nouveau modèle
      const res = await api.get(`/admin/formbuilder/format/formalities/${tpl.ftId}`);

      const oldMeta = res.data?.format?.meta;
      if (!oldMeta) {
        console.error("Format invalide reçu :", res.data);
        return;
      }

      setMetaEditor({
        open: true,
        mode: "duplicate",
        meta: {
          ...oldMeta,
          ftId: null,
          ftStatus: "inactive",
          ftName: `${oldMeta.ftName} – copie`
        }
      });
    } catch (e) {
      console.error("Erreur duplication :", e);
    }
  };

  // ----------------------------------------------------------------------
  // SAVE META
  // ----------------------------------------------------------------------
  const saveMeta = async (meta) => {
    try {
      // CREATE
      if (!meta.ftId) {
        const res = await api.post("/admin/formalities/template", { meta });
        if (res.data?.template) {
          setTemplates([...templates, res.data?.template]);
        }

      }
      // UPDATE
      else {
        const res = await api.put(
          `/admin/formalities/template/${meta.ftId}`,
          { meta }
        );
        if (res.status === 200) {
          setTemplates((prev) =>
            prev.map((t) => (t.ftId === meta.ftId ? { ...t, ...meta } : t))
          );
        }
      }
    } catch (e) {
      console.error("Erreur save meta :", e);
    } finally {
      setMetaEditor({ open: false, meta: null, mode: "create" });
    }
  };

  // ----------------------------------------------------------------------
  // ARCHIVE
  // ----------------------------------------------------------------------
  const handleArchive = async (tpl) => {
    const confirm = await Swal.fire({
      title: "Archiver cette formalité ?",
      text: `La formalité « ${tpl.ftName} » sera archivée.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Oui, archiver",
      cancelButtonText: "Annuler"
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.put(`/admin/formalities/template/archive/${tpl.ftId}`);
      setTemplates((prev) => prev.filter((t) => t.ftId !== tpl.ftId));
    } catch (e) {
      Swal.fire("Erreur", "Archivage impossible", "error");
    }
  };

  // ----------------------------------------------------------------------
  // FILTERING
  // ----------------------------------------------------------------------
  const normalize = (str = "") =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates;
    const q = normalize(search);

    return templates.filter(
      (tpl) =>
        normalize(tpl.ftName).includes(q) ||
        normalize(tpl.ftCreatedBy || "").includes(q)
    );
  }, [templates, search]);

  const templatesByCategory = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      const list = filteredTemplates.filter(
        (tpl) => tpl.ftCategoryId === cat.fcId
      );

      const activeCount = list.filter(
        (tpl) => tpl.ftStatus === "active"
      ).length;

      map[cat.fcId] = {
        category: cat,
        templates: list,
        activeCount,
        inactiveCount: list.length - activeCount
      };
    });
    return map;
  }, [categories, filteredTemplates]);

  // ----------------------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------------------
  return (
    <div id="admin-formalities-templates-list" className="py-4 container-fluid pe-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h4 m-0"><span>Catalogue des formalités</span></h1>
        <button className="btn btn-primary" onClick={openCreate}>
          <FaPlus /> Nouveau template
        </button>
      </div>
      <div className="container">
        <input
          className="form-control form-control-lg mb-4"
          placeholder="Rechercher un template..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading && <p>Chargement…</p>}
        {error && <p className="text-danger">{error}</p>}

        {!loading && !error && (
          <div className="accordion" id="templatesAccordion">
            {categories.map((cat) => {
              const group = templatesByCategory[cat.fcId] || {
                templates: [],
                activeCount: 0,
                inactiveCount: 0
              };

              return (
                <div className="accordion-item" key={cat.fcId}>
                  <h2 className="accordion-header">
                    <button
                      className="accordion-button collapsed d-flex"
                      data-bs-toggle="collapse"
                      data-bs-target={`#cat-${cat.fcId}`}
                      onClick={() => {
                        const newValue = openCategoryId == cat.fcId ? null : cat.fcId;
                        setOpenCategoryId(newValue);
                        if (newValue) {
                          localStorage.setItem(STORAGE_KEY, newValue);
                        } else {
                          localStorage.removeItem(STORAGE_KEY);
                        }
                      }}
                    >
                      <strong className="d-inline-block flex-grow-1">{cat.fcName}</strong>
                      <span className="ms-auto me-3 d-flex gap-2 d-inline-block  flex-grow-0">
                        <span className="badge bg-success">{group.activeCount}</span>
                        <span className="badge bg-secondary">{group.inactiveCount}</span>
                      </span>
                    </button>
                  </h2>
                  <div
                    id={`cat-${cat.fcId}`}
                    className={`accordion-collapse collapse ${openCategoryId == cat.fcId ? "show" : ""
                      }`}
                  >
                    <div className="accordion-body p-0">
                      {group.templates.map((tpl) => (
                        <div key={tpl.ftId} className="list-group-item">
                          <div className="d-flex align-items-center gap-3">
                            <div className="flex-grow-1 ps-4 py-2 pe-2">
                              {tpl.ftName}
                            </div>
                            <span
                              className={`badge px-3 ${tpl.ftStatus === "active"
                                ? "bg-success"
                                : "bg-secondary"

                                }`}
                            >
                              {tpl.ftStatus == 'active' ? 'En ligne' : 'Brouillon'}
                            </span>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-secondary"
                                onClick={() => openEditMeta(tpl)}
                              >
                                <FiSettings />
                              </button>
                              <button
                                className="btn btn-outline-secondary"
                                onClick={() =>
                                  navigate(
                                    `/admin/formalities/template/builder/${tpl.ftId}`
                                  )
                                }
                              >
                                <FiSliders />
                              </button>
                              <button
                                className="btn btn-outline-secondary d-none"
                                onClick={() => openDuplicate(tpl)}
                              >
                                <FiFileText />
                              </button>

                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        )}
      </div>

      {metaEditor.open && (
        <Modal
          show
          size="lg"
          onHide={() =>
            setMetaEditor({ open: false, meta: null, mode: "create" })
          }
        >
          <Modal.Body>
            <EditorFormalitiesMeta
              initialMeta={metaEditor.meta}
              mode={metaEditor.mode}
              onSave={saveMeta}
              onCancel={() =>
                setMetaEditor({ open: false, meta: null, mode: "create" })
              }
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}
